// src/context/OverlayManagerContext.js
// Global overlay manager to ensure only one overlay/modal/quickbar is open at a time

import React, { createContext, useContext, useState, useCallback } from 'react';

const OverlayManagerContext = createContext(null);

export function OverlayManagerProvider({ children }) {
    const [activeOverlay, setActiveOverlay] = useState(null);

    const openOverlay = useCallback((overlayId) => {
        setActiveOverlay(overlayId);
    }, []);

    const closeOverlay = useCallback((overlayId) => {
        // Use functional update to avoid stale closure issues
        setActiveOverlay(prev => {
            if (prev === overlayId) {
                return null;
            }
            return prev; // Don't close if it's not the active overlay
        });
    }, []); // Remove activeOverlay dependency to avoid stale closures

    const closeAllOverlays = useCallback(() => {
        setActiveOverlay(null);
    }, []);

    const isOverlayOpen = useCallback((overlayId) => {
        return activeOverlay === overlayId;
    }, [activeOverlay]);

    const value = {
        activeOverlay,
        openOverlay,
        closeOverlay,
        closeAllOverlays,
        isOverlayOpen
    };

    return (
        <OverlayManagerContext.Provider value={value}>
            {children}
        </OverlayManagerContext.Provider>
    );
}

export function useOverlayManager() {
    const context = useContext(OverlayManagerContext);
    if (!context) {
        // Return a no-op implementation if context is not available
        return {
            activeOverlay: null,
            openOverlay: () => {},
            closeOverlay: () => {},
            closeAllOverlays: () => {},
            isOverlayOpen: () => false
        };
    }
    return context;
}

// Overlay IDs constants
export const OVERLAY_IDS = {
    ADMIN_HAMBURGER: 'admin-hamburger',
    VENUES_TOAST: 'venues-toast',
    SETTINGS_DRAWER: 'settings-drawer',
    PACKERS_QUICKBAR: 'packers-quickbar',
    /** Packers Njord Scales pill — live weight / tare / calibrate / commit */
    PACKERS_SCALES: 'packers-scales',
    /** Packers compact print host (FAB stack) — mutually exclusive with Menu / Quick Bar / Queue */
    PACKERS_PRINT_HOST: 'packers-print-host',
    SENSOR_QUICKBAR: 'sensor-quickbar',
    PRODUCTION_QUICKBAR: 'production-quickbar',
    /** Production Toolkit QR snackbar (FAB stack) — mutually exclusive with Menu / Quick Slice */
    PRODUCTION_QR: 'production-qr',
    CONTACTS_QUICKBAR: 'contacts-quickbar',
    ADMIN_DASHBOARD_PLACE_ORDER: 'admin-dashboard-place-order',
    ADMIN_DASHBOARD_INSIGHTS: 'admin-dashboard-insights',
    ADMIN_DASHBOARD_USERS: 'admin-dashboard-users',
    ADMIN_DASHBOARD_TOOLS: 'admin-dashboard-tools',
    PRINT_QUEUE: 'print-queue',
    OPERATOR_MESSAGES: 'operator-messages',
    OPERATOR_DRIVE: 'operator-drive',
    MODAL: 'modal' // Generic modal overlay
};
